import React, { useEffect, useState } from 'react';
import AutoParTable, { HeaderItem, RowItem, ReportState } from '../components/AutoParTable';
import { autoParStore } from '../../../stores/autoparStore';
import { observer } from 'mobx-react-lite';
// import Paging from '../components/Paging';
import CriteriaPanel from '../components/CriteriaPanel';
import OppModelerPanel from '../components/OppModelerPanel';
import { computeMonthlyOrders, getAutoPar } from '../../../utils/autoparCalc';
import { saveParLevel } from '../../../api/autopar';
import { toast } from 'react-toastify';
import {QSightPageHeader} from '@qsightnpm/qsight-component-library';
import '../styles/autopar.css';

function AutoParPage() {
  const s = autoParStore;
  const [showCriteria, setShowCriteria] = useState(false);
  const [showModeler, setShowModeler] = useState(false);

  useEffect(() => {
    s.load();
  }, [s.filters.currentPage, s.filters.sortColumn, s.filters.sortOrder, s.filters.frequencySelection, s.filters.frequency, s.filters.reservePercent, s]);

  // Map field sort keys to legacy column indices
  const sortKeyToColumn: Record<string, number> = {
    manufacturer_name: 1,
    product_type: 2,
    catalog_no: 3,
    brand_name: 4,
    description: 5,
    UsagePerMonth: 6,
    quantity: 7,
    quantity_on_order: 8,
    par_level: 9,
    OrdersPerMonths: 10,
  };

  const reportState: ReportState = {
    loading: s.loading,
    sortKey: Object.entries(sortKeyToColumn).find(([, col]) => col === s.filters.sortColumn)?.[0],
    sortOrder: s.filters.sortOrder === 0 ? 'ASC' : 'DESC',
    setSortKey: (key?: string) => {
      if (!key) return;
      const col = sortKeyToColumn[key] ?? 0;
      const nextOrder: 0 | 1 = s.filters.sortColumn === col ? (s.filters.sortOrder === 0 ? 1 : 0) : 0;
      s.setSort(col, nextOrder);
    },
    setSortOrder: (order) => {
      const numeric = order === 'ASC' ? 0 : 1;
      s.setSort(s.filters.sortColumn, numeric);
    },
    pageIndex: s.filters.currentPage - 1,
    pageSize: s.filters.rowsPerPage,
    totalCount: s.totalCount,
    setPageSize: (size: number) => s.setFilters({ rowsPerPage: size }),
    setPageIndex: (index: number) => s.setPage(index + 1),
  };

  const allColumns: { id: string; header: HeaderItem<RowItem> }[] = [
    { id: 'Manufacturer', header: { data: 'Manufacturer', dataRef: 'manufacturerName', sortKey: 'manufacturer_name' } },
    { id: 'ProdType', header: { data: 'Product Type', dataRef: 'productType', sortKey: 'product_type' } },
    { id: 'CatNum', header: { data: 'Catalog Number', dataRef: 'catalogNo', sortKey: 'catalog_no' } },
    { id: 'Brand', header: { data: 'Brand Name', dataRef: 'brandName', sortKey: 'brand_name' } },
    { id: 'Desc', header: { data: 'Description', dataRef: 'description', sortKey: 'description', width: '20%' } },
    { id: 'Usage', header: { data: 'Avg Usage / Mo.', dataRef: 'usagePerMonth', sortKey: 'UsagePerMonth' } },
    { id: 'Qty', header: { data: 'Current Qty', dataRef: 'quantity', sortKey: 'quantity' } },
    { id: 'OnOrder', header: { data: 'On Order', dataRef: 'quantityOnOrder', sortKey: 'quantity_on_order' } },
    { id: 'Par', header: { data: 'Current Par', dataRef: 'parLevel', sortKey: 'par_level' } },
    { id: 'Freq', header: { data: 'Order Freq / Mo.', dataRef: 'ordersPerMonth', sortKey: 'OrdersPerMonths' } },
    { id: 'MaxUsage', header: { data: 'Max Month Usage', dataRef: 'maxMonthUsage', sortable: false } },
    { id: 'Suggested', header: { data: 'Suggested Par', dataRef: 'suggestedPar', sortable: false } },
    { id: 'LastUsed', header: { data: 'Last Used', dataRef: 'lastUsed', sortable: false } },
    { id: 'Actions', header: { data: 'Actions', dataRef: 'actions', sortable: false, width: 140 } },
  ];

  const headers: HeaderItem<RowItem>[] = allColumns
    .filter(c => s.filters.colIds.includes(c.id))
    .map(c => c.header);

  const rows: RowItem[] = s.records.map(r => {
    const monthOrders = computeMonthlyOrders(r.ordersPerMonth, s.filters.frequencySelection, s.filters.frequency);
    const suggested = getAutoPar(r.maxMonthUsage, r.usagePerMonth, monthOrders, s.filters.reservePercent);
    const handleSave = async () => {
      try {
        await saveParLevel(r.manufacturerId, r.catalogNo, suggested);
        toast.success(`Saved suggested par (${suggested}) for ${r.catalogNo}`);
      } catch (e) {
        toast.error('Failed to save par');
      }
    };
    return ({
      id: `${r.manufacturerId}-${r.catalogNo}`,
      manufacturerName: r.manufacturerName,
      productType: r.productType,
      catalogNo: r.catalogNo,
      brandName: r.brandName,
      description: r.description,
      usagePerMonth: <span className='text-end d-block'><span className='badge bg-light text-dark'>{r.usagePerMonth}</span></span>,
      quantity: <span className='text-end d-block'><span className='badge bg-secondary'>{r.quantity}</span></span>,
      quantityOnOrder: <span className='text-end d-block'><span className='badge bg-info text-dark'>{r.quantityOnOrder}</span></span>,
      parLevel: <span className='text-end d-block'><span className='badge bg-primary'>{r.parLevel}</span></span>,
      ordersPerMonth: <span className='text-end d-block'><span className='badge bg-light text-dark'>{r.ordersPerMonth}</span></span>,
      maxMonthUsage: <span className='text-end d-block'><span className='badge bg-warning text-dark'>{r.maxMonthUsage}</span></span>,
      suggestedPar: <span className='text-end d-block'><span className='badge bg-success'>{suggested}</span></span>,
      lastUsed: <span className='text-center d-block'><small className='text-muted'>{r.lastUsedDate ?? '—'}</small></span>,
      actions: (
        <div className='d-flex gap-2 justify-content-center'>
          <button className='btn btn-sm btn-outline-success autopar-action' title='Save Suggested Par' onClick={handleSave}>
            <i className='bi bi-save'></i>
          </button>
          <button className='btn btn-sm btn-outline-secondary autopar-action' title='Details' onClick={() => toast.info('Details coming soon') }>
            <i className='bi bi-eye'></i>
          </button>
        </div>
      ),
    });
  });

  return (
    <div className='container-fluid autopar-container'>
      <QSightPageHeader
                        title={'AutoPar™'}
                        subtitle={''}
                        summary={''}
                    />
      <div className='filter-bar d-flex flex-wrap align-items-center gap-2 mb-3'>
        <div className='btn-group'>
          <button className='btn btn-secondary' onClick={() => setShowCriteria(v => !v)}><i className='bi bi-sliders'></i> Criteria</button>
          <button className='btn btn-secondary' onClick={() => setShowModeler(v => !v)}><i className='bi bi-graph-up-arrow'></i> Opportunity Modeler™</button>
        </div>
        <div className='btn-group ms-auto'>
          <button className='btn btn-outline-primary' onClick={() => {/* hook export API later */}}><i className='bi bi-download'></i> Export</button>
          <button className='btn btn-outline-secondary' onClick={() => s.load()}><i className='bi bi-arrow-repeat'></i> Refresh</button>
        </div>
      </div>

      {showCriteria && (
        <CriteriaPanel initialColIds={s.filters.colIds} onApply={(cols) => { s.setColumns(cols); setShowCriteria(false); }} />
      )}

      {showModeler && (
        <OppModelerPanel
          frequencySelection={s.filters.frequencySelection}
          frequency={s.filters.frequency}
          reservePercent={s.filters.reservePercent}
          onApply={(fs, freq, reserve) => { s.setFilters({ frequencySelection: fs, frequency: freq, reservePercent: reserve }); setShowModeler(false); }}
        />
      )}

      <AutoParTable
        headers={headers}
        rows={rows}
        reportState={reportState}
      />

      {/* Pagination rendered by QSightDataTable using component library */}
    </div>
  );
}

export default observer(AutoParPage);
